import {
  CircuitSchema,
  CircuitNode,
  CircuitEdge,
  Annotation,
  SimulationParams,
  OperatingScenario,
  ComponentType,
  CharacteristicCurve,
  ComponentOverride,
} from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Escape special XML characters in text content / attribute values. */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Produce a pretty-printed XML string from a Document.
 * The built-in XMLSerializer does not indent, so we walk the tree manually.
 */
function prettyPrint(doc: Document): string {
  const INDENT = '  ';

  function serializeNode(node: Node, depth: number): string {
    const pad = INDENT.repeat(depth);

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      // Skip whitespace-only text nodes that we may have introduced
      if (text.trim().length === 0) return '';
      return pad + escapeXml(text.trim()) + '\n';
    }

    if (node.nodeType === Node.COMMENT_NODE) {
      return pad + '<!--' + (node.textContent ?? '') + '-->\n';
    }

    if (node.nodeType === Node.DOCUMENT_NODE) {
      let out = '<?xml version="1.0" encoding="UTF-8"?>\n';
      node.childNodes.forEach((child) => {
        out += serializeNode(child, depth);
      });
      return out;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as Element;
    const tag = el.tagName;
    let out = pad + '<' + tag;

    // Attributes
    for (let i = 0; i < el.attributes.length; i++) {
      const attr = el.attributes[i];
      out += ` ${attr.name}="${escapeXml(attr.value)}"`;
    }

    // Children
    const children = Array.from(el.childNodes);
    const hasElementChildren = children.some(
      (c) => c.nodeType === Node.ELEMENT_NODE
    );
    const hasTextChildren = children.some(
      (c) => c.nodeType === Node.TEXT_NODE && (c.textContent ?? '').trim().length > 0
    );

    if (children.length === 0) {
      out += ' />\n';
    } else if (!hasElementChildren && hasTextChildren) {
      // Inline text content — no extra newlines
      const text = el.textContent ?? '';
      out += '>' + escapeXml(text) + '</' + tag + '>\n';
    } else {
      out += '>\n';
      children.forEach((child) => {
        out += serializeNode(child, depth + 1);
      });
      out += pad + '</' + tag + '>\n';
    }

    return out;
  }

  return serializeNode(doc, 0);
}

// ---------------------------------------------------------------------------
// Serialization (CircuitSchema -> XML)
// ---------------------------------------------------------------------------

function createEl(doc: Document, tag: string, attrs?: Record<string, string>): Element {
  const el = doc.createElement(tag);
  if (attrs) {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
}

function addTextEl(doc: Document, parent: Element, tag: string, value: string): Element {
  const el = doc.createElement(tag);
  el.textContent = value;
  parent.appendChild(el);
  return el;
}

/**
 * Serialize an arbitrary value into XML elements.
 * - primitives become text elements with a `type` attribute
 * - arrays become `<items>` containers with `<item>` children
 * - objects become containers with child elements named after keys
 * - null/undefined become empty elements with type="null"
 */
function serializeValue(doc: Document, parent: Element, tag: string, value: any): void {
  if (value === null || value === undefined) {
    const el = createEl(doc, tag, { type: 'null' });
    parent.appendChild(el);
    return;
  }

  if (Array.isArray(value)) {
    const el = createEl(doc, tag, { type: 'array' });
    value.forEach((item) => {
      serializeValue(doc, el, 'item', item);
    });
    parent.appendChild(el);
    return;
  }

  if (typeof value === 'object') {
    const el = createEl(doc, tag, { type: 'object' });
    Object.entries(value).forEach(([key, val]) => {
      serializeValue(doc, el, key, val);
    });
    parent.appendChild(el);
    return;
  }

  // Primitive: string | number | boolean
  const el = createEl(doc, tag, { type: typeof value });
  el.textContent = String(value);
  parent.appendChild(el);
}

/** Serialize a ComponentType into XML elements. */
function serializeComponent(doc: Document, parent: Element, comp: ComponentType): void {
  const el = createEl(doc, 'component');

  addTextEl(doc, el, 'id', String(comp.id));
  addTextEl(doc, el, 'name', comp.name);
  addTextEl(doc, el, 'component_type', comp.component_type);
  addTextEl(doc, el, 'component_type_display', comp.component_type_display);
  addTextEl(doc, el, 'description', comp.description);
  addTextEl(doc, el, 'icon', comp.icon);
  addTextEl(doc, el, 'input_ports', String(comp.input_ports));
  addTextEl(doc, el, 'output_ports', String(comp.output_ports));
  if (comp.ports && comp.ports.length > 0) {
    serializeValue(doc, el, 'ports', comp.ports);
  }
  serializeValue(doc, el, 'default_properties', comp.default_properties);

  if (comp.curves && comp.curves.length > 0) {
    const curvesEl = createEl(doc, 'curves', { type: 'array' });
    comp.curves.forEach((curve) => {
      serializeCurve(doc, curvesEl, curve);
    });
    el.appendChild(curvesEl);
  }

  addTextEl(doc, el, 'created_at', comp.created_at);
  addTextEl(doc, el, 'updated_at', comp.updated_at);

  parent.appendChild(el);
}

/** Serialize a CharacteristicCurve. */
function serializeCurve(doc: Document, parent: Element, curve: CharacteristicCurve): void {
  const el = createEl(doc, 'curve');

  addTextEl(doc, el, 'id', String(curve.id));
  addTextEl(doc, el, 'component', String(curve.component));
  addTextEl(doc, el, 'curve_type', curve.curve_type);
  addTextEl(doc, el, 'curve_type_display', curve.curve_type_display);
  serializeValue(doc, el, 'data', curve.data);

  if (curve.source_file !== null && curve.source_file !== undefined) {
    addTextEl(doc, el, 'source_file', curve.source_file);
  } else {
    const sfEl = createEl(doc, 'source_file', { type: 'null' });
    el.appendChild(sfEl);
  }

  serializeValue(doc, el, 'metadata', curve.metadata);
  addTextEl(doc, el, 'created_at', curve.created_at);
  addTextEl(doc, el, 'updated_at', curve.updated_at);

  parent.appendChild(el);
}

/** Serialize a single CircuitNode. */
function serializeNode(doc: Document, parent: Element, node: CircuitNode): void {
  const el = createEl(doc, 'node', {
    id: node.id,
    type: node.type,
  });

  // Position
  const posEl = createEl(doc, 'position', {
    x: String(node.position.x),
    y: String(node.position.y),
  });
  el.appendChild(posEl);

  // Data
  const dataEl = createEl(doc, 'data');

  // component
  serializeComponent(doc, dataEl, node.data.component);

  // custom_name
  addTextEl(doc, dataEl, 'custom_name', node.data.custom_name);

  // properties
  serializeValue(doc, dataEl, 'properties', node.data.properties);

  // max_temperatures (optional)
  if (node.data.max_temperatures) {
    const mtEl = createEl(doc, 'max_temperatures');
    if (node.data.max_temperatures.inlet !== undefined) {
      addTextEl(doc, mtEl, 'inlet', String(node.data.max_temperatures.inlet));
    }
    if (node.data.max_temperatures.outlet !== undefined) {
      addTextEl(doc, mtEl, 'outlet', String(node.data.max_temperatures.outlet));
    }
    dataEl.appendChild(mtEl);
  }

  // display_properties
  const dpEl = createEl(doc, 'display_properties');
  Object.entries(node.data.display_properties).forEach(([key, val]) => {
    addTextEl(doc, dpEl, key, String(val));
  });
  dataEl.appendChild(dpEl);

  // NOTE: simulationResult is intentionally excluded (runtime-only)

  el.appendChild(dataEl);
  parent.appendChild(el);
}

/** Serialize a single CircuitEdge. */
function serializeEdge(doc: Document, parent: Element, edge: CircuitEdge): void {
  const attrs: Record<string, string> = {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    'source-handle': edge.sourceHandle,
    'target-handle': edge.targetHandle,
  };
  if (edge.type) {
    attrs.type = edge.type;
  }

  const el = createEl(doc, 'edge', attrs);

  if (edge.data) {
    if (edge.data.controlPoints && edge.data.controlPoints.length > 0) {
      const cpListEl = createEl(doc, 'control-points');
      edge.data.controlPoints.forEach((cp) => {
        const cpEl = createEl(doc, 'point', {
          x: String(cp.x),
          y: String(cp.y),
        });
        cpListEl.appendChild(cpEl);
      });
      el.appendChild(cpListEl);
    }

    if (edge.data.labels && edge.data.labels.length > 0) {
      const labelsEl = createEl(doc, 'labels');
      edge.data.labels.forEach((lbl) => {
        const lblEl = createEl(doc, 'label', {
          offset: String(lbl.offset),
        });
        lblEl.textContent = lbl.text;
        labelsEl.appendChild(lblEl);
      });
      el.appendChild(labelsEl);
    }
  }

  parent.appendChild(el);
}

/** Serialize a single Annotation. */
function serializeAnnotation(doc: Document, parent: Element, ann: Annotation): void {
  const attrs: Record<string, string> = {
    id: ann.id,
    type: ann.type,
  };
  if (ann.attached_to) {
    attrs['attached-to'] = ann.attached_to;
  }

  const el = createEl(doc, 'annotation', attrs);

  const posEl = createEl(doc, 'position', {
    x: String(ann.position.x),
    y: String(ann.position.y),
  });
  el.appendChild(posEl);

  addTextEl(doc, el, 'text', ann.text);

  if (ann.width !== undefined && ann.height !== undefined) {
    const sizeEl = createEl(doc, 'size', {
      width: String(ann.width),
      height: String(ann.height),
    });
    el.appendChild(sizeEl);
  }

  if (ann.color) {
    addTextEl(doc, el, 'color', ann.color);
  }

  parent.appendChild(el);
}

/** Serialize SimulationParams. */
function serializeSimulationParams(
  doc: Document,
  parent: Element,
  params: SimulationParams
): void {
  const el = createEl(doc, 'simulation-params');

  if (params.fluid_id !== null && params.fluid_id !== undefined) {
    addTextEl(doc, el, 'fluid_id', String(params.fluid_id));
  } else {
    const fidEl = createEl(doc, 'fluid_id', { type: 'null' });
    el.appendChild(fidEl);
  }

  const scenariosEl = createEl(doc, 'scenarios');
  (params.scenarios ?? []).forEach((sc) => {
    serializeScenario(doc, scenariosEl, sc);
  });
  el.appendChild(scenariosEl);

  parent.appendChild(el);
}

/** Serialize a single OperatingScenario. */
function serializeScenario(
  doc: Document,
  parent: Element,
  scenario: OperatingScenario
): void {
  const el = createEl(doc, 'scenario', { id: scenario.id });

  addTextEl(doc, el, 'name', scenario.name);
  addTextEl(doc, el, 'ambient_temperature', String(scenario.ambient_temperature));

  const overridesEl = createEl(doc, 'component_overrides');
  Object.entries(scenario.component_overrides).forEach(([nodeId, override]) => {
    serializeComponentOverride(doc, overridesEl, nodeId, override);
  });
  el.appendChild(overridesEl);

  parent.appendChild(el);
}

/** Serialize a single ComponentOverride. */
function serializeComponentOverride(
  doc: Document,
  parent: Element,
  nodeId: string,
  override: ComponentOverride
): void {
  const el = createEl(doc, 'override', { 'node-id': nodeId });

  if (override.heat_input !== undefined) {
    addTextEl(doc, el, 'heat_input', String(override.heat_input));
  }
  if (override.pump_flow !== undefined) {
    addTextEl(doc, el, 'pump_flow', String(override.pump_flow));
  }

  parent.appendChild(el);
}

// ---------------------------------------------------------------------------
// Public: serialize
// ---------------------------------------------------------------------------

/**
 * Convert a CircuitSchema to a well-formed, human-readable XML string.
 */
export function serializeToXml(schema: CircuitSchema): string {
  const doc = document.implementation.createDocument(null, 'circuit-schema', null);
  const root = doc.documentElement;

  // Separate annotation nodes from regular component nodes
  const componentNodes: CircuitNode[] = [];
  const annotationNodesAsAnnotations: Annotation[] = [];
  schema.nodes.forEach((node) => {
    if (node.type === 'annotationNode') {
      // Convert React Flow annotation node to Annotation for XML
      const ann: Annotation = {
        id: node.id,
        type: (node.data as any)?.annotationType || 'label',
        position: node.position,
        text: (node.data as any)?.text || '',
      };
      if ((node.data as any)?.width != null) ann.width = (node.data as any).width;
      if ((node.data as any)?.height != null) ann.height = (node.data as any).height;
      if ((node.data as any)?.attached_to) ann.attached_to = (node.data as any).attached_to;
      if ((node.data as any)?.color) ann.color = (node.data as any).color;
      annotationNodesAsAnnotations.push(ann);
    } else {
      componentNodes.push(node);
    }
  });

  // -- Nodes (component nodes only)
  const nodesEl = createEl(doc, 'nodes');
  componentNodes.forEach((node) => serializeNode(doc, nodesEl, node));
  root.appendChild(nodesEl);

  // -- Edges
  const edgesEl = createEl(doc, 'edges');
  schema.edges.forEach((edge) => serializeEdge(doc, edgesEl, edge));
  root.appendChild(edgesEl);

  // -- Annotations (from schema.annotations + annotation nodes extracted above)
  const allAnnotations = [...(schema.annotations || []), ...annotationNodesAsAnnotations];
  const annsEl = createEl(doc, 'annotations');
  allAnnotations.forEach((ann) => serializeAnnotation(doc, annsEl, ann));
  root.appendChild(annsEl);

  // -- Viewport (optional)
  if (schema.viewport) {
    const vpEl = createEl(doc, 'viewport', {
      x: String(schema.viewport.x),
      y: String(schema.viewport.y),
      zoom: String(schema.viewport.zoom),
    });
    root.appendChild(vpEl);
  }

  // -- Simulation params (optional)
  if (schema.simulation_params) {
    serializeSimulationParams(doc, root, schema.simulation_params);
  }

  return prettyPrint(doc);
}

// ---------------------------------------------------------------------------
// Deserialization (XML -> CircuitSchema)
// ---------------------------------------------------------------------------

/** Get all direct child elements of an element, optionally filtered by tag. */
function childElements(parent: Element, tag?: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < parent.childNodes.length; i++) {
    const child = parent.childNodes[i];
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      if (!tag || el.tagName === tag) {
        result.push(el);
      }
    }
  }
  return result;
}

/** Get the first direct child element with the given tag, or null. */
function childElement(parent: Element, tag: string): Element | null {
  return childElements(parent, tag)[0] ?? null;
}

/** Get the text content of a direct child element, or a default value. */
function childText(parent: Element, tag: string, defaultValue: string = ''): string {
  const el = childElement(parent, tag);
  if (!el) return defaultValue;
  return el.textContent ?? defaultValue;
}

/** Parse a number from a direct child element's text, or return a default. */
function childNumber(parent: Element, tag: string, defaultValue: number = 0): number {
  const text = childText(parent, tag, '');
  if (text === '') return defaultValue;
  const n = Number(text);
  return isNaN(n) ? defaultValue : n;
}

/**
 * Deserialize a generic value from an element produced by serializeValue().
 * Recognizes type="null", type="string", type="number", type="boolean",
 * type="array", type="object".
 */
function deserializeValue(el: Element): any {
  const typeAttr = el.getAttribute('type');

  if (typeAttr === 'null') return null;

  if (typeAttr === 'array') {
    return childElements(el, 'item').map((itemEl) => deserializeValue(itemEl));
  }

  if (typeAttr === 'object') {
    const obj: Record<string, any> = {};
    childElements(el).forEach((child) => {
      obj[child.tagName] = deserializeValue(child);
    });
    return obj;
  }

  const text = el.textContent ?? '';

  if (typeAttr === 'number') {
    const n = Number(text);
    return isNaN(n) ? 0 : n;
  }

  if (typeAttr === 'boolean') {
    return text === 'true';
  }

  // typeAttr === 'string' or missing — treat as string
  return text;
}

/** Deserialize a CharacteristicCurve element. */
function deserializeCurve(el: Element): CharacteristicCurve {
  return {
    id: childNumber(el, 'id'),
    component: childNumber(el, 'component'),
    curve_type: childText(el, 'curve_type') as CharacteristicCurve['curve_type'],
    curve_type_display: childText(el, 'curve_type_display'),
    data: deserializeValue(childElement(el, 'data')!) as Record<string, number>[],
    source_file: (() => {
      const sfEl = childElement(el, 'source_file');
      if (!sfEl) return null;
      if (sfEl.getAttribute('type') === 'null') return null;
      return sfEl.textContent ?? null;
    })(),
    metadata: deserializeValue(childElement(el, 'metadata')!) as Record<string, any>,
    created_at: childText(el, 'created_at'),
    updated_at: childText(el, 'updated_at'),
  };
}

/** Deserialize a ComponentType element. */
function deserializeComponent(el: Element): ComponentType {
  const curvesEl = childElement(el, 'curves');
  const curves = curvesEl
    ? childElements(curvesEl, 'curve').map(deserializeCurve)
    : undefined;

  const comp: ComponentType = {
    id: childNumber(el, 'id'),
    name: childText(el, 'name'),
    component_type: childText(el, 'component_type') as ComponentType['component_type'],
    component_type_display: childText(el, 'component_type_display'),
    description: childText(el, 'description'),
    icon: childText(el, 'icon'),
    input_ports: childNumber(el, 'input_ports'),
    output_ports: childNumber(el, 'output_ports'),
    default_properties: deserializeValue(
      childElement(el, 'default_properties')!
    ) as Record<string, any>,
    created_at: childText(el, 'created_at'),
    updated_at: childText(el, 'updated_at'),
  };

  if (curves && curves.length > 0) {
    comp.curves = curves;
  }

  const portsEl = childElement(el, 'ports');
  if (portsEl) {
    const ports = deserializeValue(portsEl);
    if (Array.isArray(ports) && ports.length > 0) comp.ports = ports;
  }

  return comp;
}

/** Deserialize a CircuitNode from a <node> element. */
function deserializeNode(el: Element): CircuitNode {
  const posEl = childElement(el, 'position')!;
  const dataEl = childElement(el, 'data')!;

  const compEl = childElement(dataEl, 'component')!;
  const propsEl = childElement(dataEl, 'properties')!;
  const mtEl = childElement(dataEl, 'max_temperatures');
  const dpEl = childElement(dataEl, 'display_properties')!;

  const node: CircuitNode = {
    id: el.getAttribute('id') ?? '',
    type: el.getAttribute('type') ?? '',
    position: {
      x: Number(posEl.getAttribute('x') ?? '0'),
      y: Number(posEl.getAttribute('y') ?? '0'),
    },
    data: {
      component: deserializeComponent(compEl),
      custom_name: childText(dataEl, 'custom_name'),
      properties: deserializeValue(propsEl) as Record<string, any>,
      display_properties: {} as Record<string, boolean>,
    },
  };

  // max_temperatures
  if (mtEl) {
    const mt: { inlet?: number; outlet?: number } = {};
    const inletText = childText(mtEl, 'inlet', '');
    const outletText = childText(mtEl, 'outlet', '');
    if (inletText !== '') mt.inlet = Number(inletText);
    if (outletText !== '') mt.outlet = Number(outletText);
    node.data.max_temperatures = mt;
  }

  // display_properties
  childElements(dpEl).forEach((child) => {
    node.data.display_properties[child.tagName] = child.textContent === 'true';
  });

  return node;
}

/** Deserialize a CircuitEdge from an <edge> element. */
function deserializeEdge(el: Element): CircuitEdge {
  const edge: CircuitEdge = {
    id: el.getAttribute('id') ?? '',
    source: el.getAttribute('source') ?? '',
    target: el.getAttribute('target') ?? '',
    sourceHandle: el.getAttribute('source-handle') ?? '',
    targetHandle: el.getAttribute('target-handle') ?? '',
  };

  const typeAttr = el.getAttribute('type');
  if (typeAttr) {
    edge.type = typeAttr;
  }

  const cpEl = childElement(el, 'control-points');
  const labelsEl = childElement(el, 'labels');

  if (cpEl || labelsEl) {
    edge.data = {};

    if (cpEl) {
      edge.data.controlPoints = childElements(cpEl, 'point').map((ptEl) => ({
        x: Number(ptEl.getAttribute('x') ?? '0'),
        y: Number(ptEl.getAttribute('y') ?? '0'),
      }));
    }

    if (labelsEl) {
      edge.data.labels = childElements(labelsEl, 'label').map((lblEl) => ({
        text: lblEl.textContent ?? '',
        offset: Number(lblEl.getAttribute('offset') ?? '0'),
      }));
    }
  }

  return edge;
}

/** Deserialize an Annotation from an <annotation> element. */
function deserializeAnnotation(el: Element): Annotation {
  const posEl = childElement(el, 'position')!;

  const ann: Annotation = {
    id: el.getAttribute('id') ?? '',
    type: (el.getAttribute('type') ?? 'label') as Annotation['type'],
    position: {
      x: Number(posEl.getAttribute('x') ?? '0'),
      y: Number(posEl.getAttribute('y') ?? '0'),
    },
    text: childText(el, 'text'),
  };

  const sizeEl = childElement(el, 'size');
  if (sizeEl) {
    const w = Number(sizeEl.getAttribute('width') ?? '');
    const h = Number(sizeEl.getAttribute('height') ?? '');
    if (!isNaN(w) && w > 0) ann.width = w;
    if (!isNaN(h) && h > 0) ann.height = h;
  }

  const attachedTo = el.getAttribute('attached-to');
  if (attachedTo) {
    ann.attached_to = attachedTo;
  }

  const colorText = childText(el, 'color', '');
  if (colorText) {
    ann.color = colorText;
  }

  return ann;
}

/** Deserialize a ComponentOverride from an <override> element. */
function deserializeComponentOverride(el: Element): ComponentOverride {
  const override: ComponentOverride = {};

  const hi = childText(el, 'heat_input', '');
  if (hi !== '') override.heat_input = Number(hi);

  const pf = childText(el, 'pump_flow', '');
  if (pf !== '') override.pump_flow = Number(pf);

  return override;
}

/** Deserialize an OperatingScenario from a <scenario> element. */
function deserializeScenario(el: Element): OperatingScenario {
  const overridesEl = childElement(el, 'component_overrides')!;
  const component_overrides: Record<string, ComponentOverride> = {};

  childElements(overridesEl, 'override').forEach((ovEl) => {
    const nodeId = ovEl.getAttribute('node-id') ?? '';
    component_overrides[nodeId] = deserializeComponentOverride(ovEl);
  });

  return {
    id: el.getAttribute('id') ?? '',
    name: childText(el, 'name'),
    ambient_temperature: childNumber(el, 'ambient_temperature'),
    component_overrides,
  };
}

/** Deserialize SimulationParams from a <simulation-params> element. */
function deserializeSimulationParams(el: Element): SimulationParams {
  const fluidIdEl = childElement(el, 'fluid_id');
  let fluid_id: number | null = null;
  if (fluidIdEl) {
    if (fluidIdEl.getAttribute('type') === 'null') {
      fluid_id = null;
    } else {
      const n = Number(fluidIdEl.textContent ?? '');
      fluid_id = isNaN(n) ? null : n;
    }
  }

  const scenariosEl = childElement(el, 'scenarios');
  const scenarios = scenariosEl
    ? childElements(scenariosEl, 'scenario').map(deserializeScenario)
    : [];

  return { fluid_id, scenarios };
}

// ---------------------------------------------------------------------------
// Public: deserialize
// ---------------------------------------------------------------------------

/**
 * Parse an XML string (produced by serializeToXml) back into a CircuitSchema.
 */
export function deserializeFromXml(xml: string): CircuitSchema {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');

  // Check for parse errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Invalid XML: ' + (parseError.textContent ?? 'parse error'));
  }

  const root = doc.documentElement;
  if (root.tagName !== 'circuit-schema') {
    throw new Error(
      `Expected root element <circuit-schema>, got <${root.tagName}>`
    );
  }

  // -- Nodes
  const nodesEl = childElement(root, 'nodes');
  const nodes: CircuitNode[] = nodesEl
    ? childElements(nodesEl, 'node').map(deserializeNode)
    : [];

  // -- Edges
  const edgesEl = childElement(root, 'edges');
  const edges: CircuitEdge[] = edgesEl
    ? childElements(edgesEl, 'edge').map(deserializeEdge)
    : [];

  // -- Annotations → convert to React Flow annotation nodes and merge into nodes
  const annsEl = childElement(root, 'annotations');
  const annotations: Annotation[] = annsEl
    ? childElements(annsEl, 'annotation').map(deserializeAnnotation)
    : [];

  // Convert Annotation objects into React Flow nodes so FlowCanvas can render them
  const annotationNodes = annotations.map((ann) => {
    const nodeData: any = {
      text: ann.text,
      annotationType: ann.type,
    };
    if (ann.width != null) nodeData.width = ann.width;
    if (ann.height != null) nodeData.height = ann.height;
    if (ann.attached_to) nodeData.attached_to = ann.attached_to;
    if (ann.color) nodeData.color = ann.color;
    return {
      id: ann.id,
      type: 'annotationNode',
      position: ann.position,
      data: nodeData,
    };
  });
  const allNodes = [...nodes, ...annotationNodes] as CircuitNode[];

  const schema: CircuitSchema = { nodes: allNodes, edges, annotations: [] };

  // -- Viewport (optional)
  const vpEl = childElement(root, 'viewport');
  if (vpEl) {
    schema.viewport = {
      x: Number(vpEl.getAttribute('x') ?? '0'),
      y: Number(vpEl.getAttribute('y') ?? '0'),
      zoom: Number(vpEl.getAttribute('zoom') ?? '1'),
    };
  }

  // -- Simulation params (optional)
  const spEl = childElement(root, 'simulation-params');
  if (spEl) {
    schema.simulation_params = deserializeSimulationParams(spEl);
  }

  return schema;
}
